import type { TrackerAdapter } from '../types';
import { PERF_EVENT_PREFIX, FCP_THRESHOLDS, LCP_THRESHOLDS } from '../constants';
import { isBrowser, classifyRating, getPageUrl } from '../utils';

/** Callback type for when a perf metric is captured */
export type PerfReportCallback = (
  eventName: string,
  properties: Record<string, any>,
) => void;

/**
 * Setup FCP PerformanceObserver.
 * Only called when reportFcp is true.
 */
function setupFcpObserver(onReport: PerfReportCallback): void {
  if (!isBrowser() || typeof PerformanceObserver === 'undefined') return;

  let reported = false;

  const reportFcp = (value: number) => {
    if (reported) return;
    reported = true;

    const rating = classifyRating(value, FCP_THRESHOLDS.good, FCP_THRESHOLDS.poor);

    onReport(`${PERF_EVENT_PREFIX}fcp`, {
      metric_name: 'FCP',
      value: Math.round(value),
      rating,
    });
  };

  // Try to get FCP from observer first
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          reportFcp(entry.startTime);

          observer.disconnect();
          return; // FCP only fires once
        }
      }
    });

    observer.observe({ type: 'paint', buffered: true });
  } catch {
    // PerformanceObserver not supported for paint type
  }

  // Fallback: check if FCP already happened
  try {
    const entries = performance.getEntriesByName('first-contentful-paint');
    if (entries.length > 0) {
      reportFcp(entries[0].startTime);
    }
  } catch {
    // performance API not available
  }
}

/**
 * Setup LCP PerformanceObserver.
 * Only called when reportLcp is true.
 * LCP candidates can update during initial rendering; we report the latest one after load + 1s.
 */
function setupLcpObserver(onReport: PerfReportCallback, debug = false): void {
  if (!isBrowser() || typeof PerformanceObserver === 'undefined') return;

  const entryType = 'largest-contentful-paint';
  const supportedEntryTypes = PerformanceObserver.supportedEntryTypes;
  if (supportedEntryTypes && !supportedEntryTypes.includes(entryType)) {
    if (debug) {
      console.warn('[yl-nsm-track] LCP observer is not supported in this browser.');
    }
    return;
  }

  let lcpEntry: PerformanceEntry | null = null;
  let reported = false;
  let loadTimer: ReturnType<typeof window.setTimeout> | undefined;

  const logDebug = (message: string, data?: Record<string, any>) => {
    if (!debug) return;
    if (data) {
      console.info(`[yl-nsm-track] ${message}`, data);
    } else {
      console.info(`[yl-nsm-track] ${message}`);
    }
  };

  const updateLcpEntry = (entries: PerformanceEntryList | PerformanceEntry[]): void => {
    if (entries.length === 0) return;

    // Keep the latest LCP entry only. Never fall back to FCP here.
    lcpEntry = entries[entries.length - 1];
    logDebug('LCP candidate captured.', {
      value: Math.round(lcpEntry.startTime),
    });
  };

  try {
    const observer = new PerformanceObserver((list) => {
      updateLcpEntry(list.getEntries());
    });

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('load', onLoad);
      if (loadTimer !== undefined) {
        window.clearTimeout(loadTimer);
        loadTimer = undefined;
      }
      observer.disconnect();
    };

    const flushPendingEntries = () => {
      try {
        updateLcpEntry(observer.takeRecords());
      } catch {
        // ignore takeRecords failures
      }

      try {
        updateLcpEntry(performance.getEntriesByType(entryType));
      } catch {
        // ignore performance API failures
      }
    };

    const reportLcp = (reason: string) => {
      if (reported) return;

      flushPendingEntries();
      reported = true;

      if (!lcpEntry) {
        logDebug('No LCP entry found. perf_lcp will not be reported.', { reason });
        cleanup();
        return;
      }

      const value = lcpEntry.startTime;
      const rating = classifyRating(value, LCP_THRESHOLDS.good, LCP_THRESHOLDS.poor);

      onReport(`${PERF_EVENT_PREFIX}lcp`, {
        metric_name: 'LCP',
        value: Math.round(value),
        rating,
      });

      logDebug('LCP reported.', {
        reason,
        value: Math.round(value),
      });

      lcpEntry = null;
      cleanup();
    };

    const scheduleLoadReport = () => {
      if (reported || loadTimer !== undefined) return;

      logDebug('Window load observed. LCP will be reported after 1s.');
      loadTimer = window.setTimeout(() => reportLcp('load+1s'), 1000);
    };

    const onLoad = () => {
      scheduleLoadReport();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        reportLcp('visibilitychange');
      }
    };

    const onPageHide = () => {
      reportLcp('pagehide');
    };

    observer.observe({ type: entryType, buffered: true });
    logDebug('LCP observer registered.');

    window.addEventListener('load', onLoad, { once: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    if (document.readyState === 'complete') {
      scheduleLoadReport();
    }

    if (document.visibilityState === 'hidden') {
      reportLcp('already-hidden');
    }
  } catch (error) {
    if (debug) {
      console.warn('[yl-nsm-track] Failed to setup LCP observer.', error);
    }
  }
}

/**
 * Setup performance observers based on init options.
 */
export function setupPerfObservers(
  adapter: TrackerAdapter,
  options: { reportFcp: boolean; reportLcp: boolean; debug?: boolean },
): void {
  const onReport: PerfReportCallback = (eventName, properties) => {
    adapter.event(eventName, {
      ...adapter.getDefaultProps(),
      ...properties
    });
  };

  if (options.reportFcp) {
    setupFcpObserver(onReport);
  }

  if (options.reportLcp) {
    setupLcpObserver(onReport, options.debug === true);
  }
}
